import {
  appInvestmentsToProfileInvestments,
  appAccountsToProfileAccounts,
  convertToChosenInvestments,
  custodianHoldings,
  getResidentialAddressForEnabled,
  filterMailingAddressForEnabled,
  setInvestmentFundName
} from '../shared/services/util';
import { isEmpty, withDefault, APP_NAME, APP_VERSION } from '../../core/util';
import {
  applicationStatusTypes,
  accountTypes,
  setTaxIDType,
  parsePhone,
  setRJCustomerPhones,
  getMailingAddressDifferentFlag
} from '../shared/utils';
import { PartialStateStore } from '../../core/types/application-state';
import {
  StaticExistingAccount,
  StaticInvestment
} from '../../core/types/investment';
import { DeepPartialProfileArgs } from '../../core/types/return-journey-profile';

export const parseExistingInvestments = (
  profileInvestments
): StaticExistingAccount[] | [] => {
  if (!profileInvestments || !profileInvestments.length) return [];
  const parsedExistingInvestments = profileInvestments.filter(
    inv => inv.agreementID && inv.agreementID !== ''
  );

  const existingAccounts = parsedExistingInvestments.map(
    ({
      ticker,
      trpCode,
      fundingAmount,
      fundingAmountPercentage,
      agreementID
    }) => ({
      investmentVehicle: {
        ticker,
        trpCode
      },
      agreementID,
      fundingDetails: {
        fundingAmount,
        fundingAmountPercentage
      }
    })
  );

  return existingAccounts;
};

export const parseNewInvestments = (profileInvestments): StaticInvestment[] => {
  if (!profileInvestments || !profileInvestments.length) return [];
  const parsedNewInvestments = profileInvestments.filter(
    inv => !inv.agreementID || inv.agreementID === ''
  );

  const newInvestments = parsedNewInvestments.map(
    ({
      fundName,
      ticker,
      trpCode,
      fundingAmount,
      fundingAmountPercentage
    }) => ({
      fundName,
      ticker,
      trpCode,
      fundingDetails: {
        fundingAmount,
        fundingAmountPercentage
      }
    })
  );

  return newInvestments;
};

export const convertAppDataToProfileDataTransfer = (
  store: PartialStateStore
): DeepPartialProfileArgs => {
  const { app, chosenInvestments, progressStepText } = store;

  // add fund name to investment allocations; needed for form request
  const updatedInvestmentAllocations = setInvestmentFundName(
    chosenInvestments,
    app?.newInvestments
  );

  const newInvestments = appInvestmentsToProfileInvestments(
    updatedInvestmentAllocations
  );

  const existingAccounts = appAccountsToProfileAccounts(app?.existingAccounts);

  // TODO [2020-05-25]: Let's make this a util to combine new + existing investments for profile service
  const investmentAllocations = [...existingAccounts, ...newInvestments];

  return {
    applicationID: withDefault(app?.profileApplicationID, ''),
    emailAddress: app?.customer?.primaryEmailAddress,
    applicationStatus: withDefault(
      app?.applicationStatus,
      applicationStatusTypes.IN_PROGRESS
    ),
    investmentAllocations,
    custodianInformation: [
      {
        custodianID: app?.custodian?.custodianID,
        custodianName: app?.custodian?.custodianName,
        address: {
          street1: withDefault(app?.custodian?.custodianAddress?.street1, ''),
          street2: withDefault(app?.custodian?.custodianAddress?.street2, ''),
          state: withDefault(app?.custodian?.custodianAddress?.state, ''),
          city: withDefault(app?.custodian?.custodianAddress?.city, ''),
          zip: withDefault(app?.custodian?.custodianAddress?.zip, ''),
          usaddress: true,
          foreignAddress: false,
          militaryAddress: false
        },
        phone: withDefault(store?.custodianCustomerServicePhone, ''),
        holdings: withDefault(custodianHoldings(app?.externalAccounts), []),
        toraParticipant: withDefault(store?.toraParticipant, false),
        // TODO [2021-01-22]: there needs to be a story to base
        // useAccountNumberForAllHoldings
        // on the actual checkbox value in the UI
        // rather than hardcoding it to false every time
        useAccountNumberForAllHoldings: false,
        brokerageAccount: withDefault(store?.fundsInBrokerage, false),
        liquidAssets: withDefault(store?.preliquidated, false),
        branchAddressRequired: withDefault(
          store?.loadedData?.branchAddressRequired,
          false
        )
      }
    ],
    // ToDo: Below types need to be added and pulled in from store w.r.t current flow
    accountType: accountTypes.TRANSFER,
    toIRAAccountType: app?.toIRAAccountType,
    fromIRAAccountType: app?.fromIRAAccountType,
    investAmount: withDefault(store?.amount, 0),
    firstName: app?.customer?.name?.firstName,
    lastName: app?.customer?.name?.lastName,
    middleName: withDefault(app?.customer?.name?.middleName, ''),
    residentialAddress: {
      street1: app?.customer?.residentialAddress?.street1,
      street2: app?.customer?.residentialAddress?.street2,
      state: app?.customer?.residentialAddress?.state,
      zip: app?.customer?.residentialAddress?.zip,
      city: app?.customer?.residentialAddress?.city
    },
    mailingAddress: {
      street1: app?.customer?.mailingAddress?.street1,
      street2: app?.customer?.mailingAddress?.street2,
      state: app?.customer?.mailingAddress?.state,
      zip: app?.customer?.mailingAddress?.zip,
      city: app?.customer?.mailingAddress?.city
    },
    citizenship: withDefault(app?.customer?.citizenship, 'UNKNOWN'),
    dateOfBirth: app?.customer?.dob,
    taxID: withDefault(app?.customer?.taxID, ''),
    phoneNumbers:
      app?.customer?.phones?.map(({ areaCode, exchange, lastFour, type }) => ({
        phoneNumber: `(${areaCode}) ${exchange}-${lastFour}`,
        phoneNumberType: type
      })) || [],
    investmentSelectionPath: app?.investmentMethod,
    residentialAddressDifferentFromMailing: withDefault(
      store?.mailingAddressDifferent,
      false
    ),
    // ToDo: Add termIDs on the review page submission
    termsIDs: withDefault(
      app?.termsIDs?.map(id => id?.toString()),
      []
    ),
    consumerVersion: APP_VERSION,
    consumerName: APP_NAME,
    progressStepText,
    progressStep: withDefault(store?.progressStep, 0)
  };
};

export const convertProfileDataToAppDataTransfer = async (
  profile: DeepPartialProfileArgs,
  store: PartialStateStore
): Promise<PartialStateStore> => {
  // account for logged in user customer data
  // customer data should take precedence over journey profile customer data
  const { app, enabled } = store;
  const phones = setRJCustomerPhones(
    profile?.phoneNumbers,
    app?.customer?.phones
  );

  return {
    accountType: profile?.accountType,
    amount: profile?.investAmount,
    app: {
      customer: enabled
        ? {
            ...app?.customer,
            citizenship: profile?.citizenship,
            taxIDType: setTaxIDType(profile?.citizenship),
            residentialAddress: getResidentialAddressForEnabled(
              app?.customer?.residentialAddress,
              profile?.residentialAddress
            ),
            // pull email, phone and mailing address from profile service to
            // account for RPS only users submitting via FONASU RJ
            // since the customer call while doing RJ is not pulling them currently
            mailingAddress: withDefault(
              profile?.mailingAddress,
              filterMailingAddressForEnabled(app?.customer?.mailingAddress)
            ),
            primaryEmailAddress: withDefault(
              profile?.emailAddress,
              app?.customer?.primaryEmailAddress
            ),
            phones
          }
        : {
            name: {
              firstName: profile?.firstName,
              middleName: withDefault(profile?.middleName, ''),
              lastName: profile?.lastName
            },
            citizenship: profile?.citizenship,
            dob: profile?.dateOfBirth,
            taxID: profile?.taxID,
            taxIDType: setTaxIDType(profile?.citizenship),
            residentialAddress: {
              street1: profile?.residentialAddress?.street1,
              street2: profile?.residentialAddress?.street2,
              state: profile?.residentialAddress?.state,
              zip: profile?.residentialAddress?.zip,
              city: profile?.residentialAddress?.city
            },
            mailingAddress: {
              street1: profile?.mailingAddress?.street1,
              street2: profile?.mailingAddress?.street2,
              state: profile?.mailingAddress?.state,
              zip: profile?.mailingAddress?.zip,
              city: profile?.mailingAddress?.city
            },
            primaryEmailAddress: profile?.emailAddress,
            // [{phoneNumberType: string, phoneNumber: string}]
            // needs to be converted to ->
            // [{ areaCode: string, exchange: string, lastFour: string, type: string }]
            //
            // TODO [2020-06-20]: This is a big pain why can't backend use same format
            phones: profile?.phoneNumbers?.map(
              ({ phoneNumberType: type = '', phoneNumber = '' }) => ({
                ...parsePhone(phoneNumber),
                type
              })
            )
          },
      existingAccounts: parseExistingInvestments(
        profile?.investmentAllocations
      ),
      // TODO [2020-06-20] If NASU only allows one custodian per application
      // why does the RJ profile service accept an array of custodians?
      // this should be consistent.
      // @note will Plaid allow us to support funding from multiple
      // custodians?
      externalAccounts: profile?.custodianInformation?.[0]?.holdings?.map(
        ({ fundName, accountNumber, amount, fundingPortion, cusip }) => ({
          accountName: fundName,
          accountType: 'MUTUAL_FUND',
          accountNumber,
          estimatedTransferAmount: amount,
          liquidationOption: fundingPortion,
          cusip
        })
      ),
      newInvestments: parseNewInvestments(profile?.investmentAllocations),
      investmentMethod: profile?.investmentSelectionPath,
      profileApplicationID: profile?.applicationID,
      toIRAAccountType: profile?.toIRAAccountType,
      fromIRAAccountType: profile?.fromIRAAccountType,
      // termsIDs have to be string inside the UI store but numbers in consent service
      // TODO: allow for numbers in service
      termsIDs: withDefault(
        profile?.termsIDs?.map(id => id?.toString()),
        []
      ),
      custodian: {
        ...(!isEmpty(profile?.custodianInformation?.[0]?.custodianID) && {
          custodianID: profile?.custodianInformation?.[0]?.custodianID
        }),
        custodianName: profile?.custodianInformation?.[0]?.custodianName,
        ...(!isEmpty(profile?.custodianInformation?.[0]?.address?.street1) && {
          custodianAddress: {
            street1: profile?.custodianInformation?.[0]?.address?.street1,
            street2: profile?.custodianInformation?.[0]?.address?.street2,
            state: profile?.custodianInformation?.[0]?.address?.state,
            zip: profile?.custodianInformation?.[0]?.address?.zip,
            city: profile?.custodianInformation?.[0]?.address?.city
          }
        })
      }
    },
    mailingAddressDifferent: getMailingAddressDifferentFlag(
      enabled,
      store,
      profile
    ),
    chosenInvestments: await convertToChosenInvestments(
      profile?.investmentAllocations,
      profile?.investmentSelectionPath,
      app?.customer?.dob,
      enabled
    ),
    progressStepText: profile?.progressStepText,
    progressStep: withDefault(profile?.progressStep, 0),
    consumerVersion: profile?.consumerVersion,
    consumerName: profile?.consumerName,
    preliquidated: profile?.custodianInformation?.[0]?.liquidAssets,
    fundsInBrokerage: profile?.custodianInformation?.[0]?.brokerageAccount,
    toraParticipant: profile?.custodianInformation?.[0]?.toraParticipant,
    custodianCustomerServicePhone: profile?.custodianInformation?.[0]?.phone,
    loadedData: {
      branchAddressRequired: withDefault(
        profile?.custodianInformation?.[0]?.branchAddressRequired,
        false
      )
    }
  };
};
